/**
 * runOutcome — pure derivations from an Orchestrator run record.
 *
 * The Goals orchestrator does not record "propose" as a separate step: the
 * run's WRITE steps ARE the proposals it queued for approval. So the propose
 * outcome is derived, honestly, straight from the run's `steps` — no extra
 * bookkeeping to drift. This keeps the run card's PROPOSE line and its
 * [Review in Approvals] chip trustworthy: they only ever reflect real steps.
 */

import type { OrchestratorRun } from '../db/types';

export interface ProposeSummary {
  /** How many proposals (write steps) this run queued for approval. */
  count: number;
  /** One-line outcome for the run card's PROPOSE line. */
  text: string;
  /** Whether to offer a [Review in Approvals] chip (only when count > 0). */
  showReviewChip: boolean;
}

/**
 * Derive the propose outcome from a run: each WRITE step is one proposal it
 * queued. When zero, the run "ran clean — nothing needed"; otherwise it
 * queued N follow-ups awaiting approval.
 */
export function proposeSummary(run: OrchestratorRun): ProposeSummary {
  const count = run.steps.filter((s) => s.kind === 'write').length;
  if (count === 0) {
    return { count: 0, text: 'ran clean — nothing needed', showReviewChip: false };
  }
  const noun = count === 1 ? 'follow-up' : 'follow-ups';
  return {
    count,
    text: `${count} ${noun} queued for your approval`,
    showReviewChip: true,
  };
}
